import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Tag, TagDraft } from './tag';
import { IdObject } from 'src/app/core/id-object';

@Injectable({
  providedIn: 'root',
})
export class TagService {
  private static listUrl = (apiUrl: string): string => `${apiUrl}tag`;
  private static singleUrl = (apiUrl: string, id: string): string =>
    `${apiUrl}tag/${id}`;
  private static searchUrl = (apiUrl: string): string => `${apiUrl}tag/search`;

  constructor(private httpClient: HttpClient) {}

  public getList(): Observable<Tag[]> {
    const { apiUrl } = environment;
    const url = TagService.listUrl(apiUrl);

    return this.httpClient
      .get<Tag[]>(url)
      .pipe(catchError(this.handleError<Tag[]>('TagService->getList', [])));
  }

  public getSingle(id: string): Observable<Tag> {
    const { apiUrl } = environment;
    const url = TagService.singleUrl(apiUrl, id);

    return this.httpClient
      .get<Tag>(url)
      .pipe(
        catchError(this.handleError<Tag>('TagService->getSingle', undefined)),
      );
  }

  public create(tagDraft: TagDraft): Observable<IdObject> {
    const { apiUrl } = environment;
    const url = TagService.listUrl(apiUrl);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.httpClient
      .post<IdObject>(url, tagDraft, { headers })
      .pipe(
        catchError(this.handleError<IdObject>('TagService->create', undefined)),
      );
  }

  public patch(id: string, tagDraft: TagDraft): Observable<boolean> {
    const { apiUrl } = environment;
    const url = TagService.singleUrl(apiUrl, id);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.httpClient
      .patch<boolean>(url, tagDraft, { headers })
      .pipe(catchError(this.handleError<boolean>('TagService->patch', false)));
  }

  public remove(id: string): Observable<boolean> {
    const { apiUrl } = environment;
    const url = TagService.singleUrl(apiUrl, id);

    return this.httpClient.delete<void>(url).pipe(
      map(() => true),
      catchError(this.handleError('TagService->remove', false)),
    );
  }

  public search(searchData: any): Observable<Tag[]> {
    const { apiUrl } = environment;
    const url = TagService.searchUrl(apiUrl);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.httpClient
      .post<Tag[]>(url, searchData, { headers })
      .pipe(catchError(this.handleError<Tag[]>('TagService->search', [])));
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(
    operation = 'operation',
    result?: T,
  ): (error: any) => Observable<T> {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: `, error);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
